import React from 'react';

const ATTACHMENT_URL = process.env.REACT_APP_ATTACHMENT_URL;

/**
 * UserAvatar component displays a user's profile picture or initials fallback
 *
 * @param {Object} props
 * @param {Object} props.user - User object with firstName, lastName, username, profilePicturePath
 * @param {string} props.size - 'xs' (20px), 'sm' (32px), 'md' (40px), 'lg' (80px)
 * @param {string} props.className - Additional CSS classes
 */
const UserAvatar = ({
  user,
  size = 'md',
  className = '',
}) => {
  const getInitials = () => {
    if (!user) return '?';

    const first = user.firstName?.charAt(0)?.toUpperCase() || '';
    const last = user.lastName?.charAt(0)?.toUpperCase() || '';

    if (first || last) {
      return `${first}${last}`;
    }

    if (user.username) {
      return user.username.charAt(0).toUpperCase();
    }

    return '?';
  };

  const getImageUrl = () => {
    if (!user?.profilePicturePath || !ATTACHMENT_URL) return null;

    // Size dimensions for image optimization
    const dimensions = {
      xs: 40, // 2x for retina
      sm: 64,
      md: 80,
      lg: 160,
    };

    const dim = dimensions[size] || dimensions.md;

    return `${ATTACHMENT_URL}/${user.profilePicturePath}?w=${dim}&h=${dim}&f=cover`;
  };

  // Size classes for Tailwind
  const sizeClasses = {
    xs: 'h-5 w-5 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-sm',
    lg: 'h-20 w-20 text-2xl',
  };

  const imageUrl = getImageUrl();
  const sizeClass = sizeClasses[size] || sizeClasses.md;

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={`${user?.firstName || 'User'}'s avatar`}
        className={`${sizeClass} rounded-full object-cover ${className}`.trim()}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} rounded-full bg-primary flex items-center justify-center text-white font-medium ${className}`.trim()}
    >
      {getInitials()}
    </div>
  );
};

export default UserAvatar;
